import { useEffect, useState } from 'react';
import type { Onboarding } from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { Button } from '../../components/ui/Button.js';
import { Modal } from '../../components/ui/Modal.js';
import { TextField } from '../../components/ui/Field.js';
import { useClickupOpportunityTypeMappings } from '../clickup/api.js';
import { useCreateCuProject } from './api.js';

interface CreateCuProjectModalProps {
  isOpen: boolean;
  onboarding: Onboarding;
  onClose: () => void;
}

/**
 * Creates a ClickUp list (a list represents a project) for the "Create CU Project"
 * onboarding step. The destination space/folder/list-template are resolved from the
 * mapping configured for the opportunity's Type on the ClickUp integration page; the
 * list name defaults to "Customer - Opportunity Type" and is editable here.
 */
export function CreateCuProjectModal({
  isOpen,
  onboarding,
  onClose,
}: CreateCuProjectModalProps): JSX.Element {
  const mappingsQuery = useClickupOpportunityTypeMappings();
  const createMutation = useCreateCuProject();
  const resetMutation = createMutation.reset;

  const [name, setName] = useState('');

  const mapping = (mappingsQuery.data ?? []).find(
    (row) => row.opportunityTypeId === onboarding.opportunityServiceCategoryId,
  );

  useEffect(() => {
    if (!isOpen) return;
    const typeName = onboarding.opportunityServiceCategoryName ?? 'Project';
    setName(`${onboarding.customerName} - ${typeName}`);
    resetMutation();
  }, [
    isOpen,
    onboarding.id,
    onboarding.customerName,
    onboarding.opportunityServiceCategoryName,
    resetMutation,
  ]);

  const handleCreate = (): void => {
    createMutation.mutate(
      { id: onboarding.id, input: { name: name.trim() } },
      { onSuccess: () => onClose() },
    );
  };

  const error =
    createMutation.error instanceof ApiError
      ? createMutation.error.message
      : createMutation.error
        ? 'Failed to create the ClickUp list.'
        : null;

  const hasType = Boolean(onboarding.opportunityServiceCategoryId);
  const hasTemplate = Boolean(mapping?.clickupListTemplateId);
  const canCreate = Boolean(name.trim() && mapping && hasTemplate);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Create ClickUp project (list)"
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={createMutation.isPending}>
            Cancel
          </Button>
          <Button onClick={handleCreate} isLoading={createMutation.isPending} disabled={!canCreate}>
            Create list
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {!hasType ? (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700">
            The opportunity has no Type set, so a ClickUp destination cannot be resolved.
          </p>
        ) : !mapping ? (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700">
            No ClickUp mapping is configured for the opportunity type{' '}
            <span className="font-medium">{onboarding.opportunityServiceCategoryName}</span>. Set
            the space, folder, and list template on the ClickUp integration page first.
          </p>
        ) : !hasTemplate ? (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700">
            The ClickUp mapping for{' '}
            <span className="font-medium">{onboarding.opportunityServiceCategoryName}</span> has no
            list template selected.
          </p>
        ) : (
          <dl className="grid grid-cols-2 gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500">Opportunity type</dt>
              <dd className="font-medium text-slate-800">
                {onboarding.opportunityServiceCategoryName}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500">Space</dt>
              <dd className="font-medium text-slate-800">{mapping.clickupSpaceName}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500">Folder</dt>
              <dd className="font-medium text-slate-800">{mapping.clickupFolderName || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500">List template</dt>
              <dd className="font-medium text-slate-800">
                {mapping.clickupListTemplateName || mapping.clickupListTemplateId}
              </dd>
            </div>
          </dl>
        )}

        <TextField
          label="New list name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Customer - Opportunity Type"
        />

        {error ? <p className="text-sm text-rose-600">{error}</p> : null}
      </div>
    </Modal>
  );
}
